import type { CSSProperties } from 'react';

import { CachedMovieData } from '@cineamo/api/models/movie/CachedMovieData.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';

import { AssetItemDto } from '../asset/AssetDto.types';
import { CustomGenre } from '../movie/GenreDto.types';
import { ImageDto } from '../movie/ImagesDto.types';
import { HEX_COLOR } from '../website-configuration/WebsiteStyle.types';

export type PageLocale = 'en' | 'de';

type Localized<T> = {
    [key in PageLocale]: T;
};

export type FileFormat = 'png' | 'gif' | 'pdf' | 'jpg' | 'mp3' | 'mov' | 'ppt' | 'pptx' | 'doc' | 'docx';

export type MovieLandingPageStyle = {
    primaryColor: HEX_COLOR;
    onPrimaryColor: HEX_COLOR;
    secondaryColor?: HEX_COLOR;
};

export type TopBannerConfiguration = {
    backgroundColor: HEX_COLOR;
    textColor?: string;
    text?: Localized<string>;
    logo?: string;
    logoHref?: string;
};

export type PressQuote = {
    quote: string;
    author: string;
    source?: string;
    rating?: number;
};

export type TeachingMaterial = {
    title: string;
    description: string;
    format: FileFormat;
    downloadLink: string;
};

/**
 * One file served under the landing page's own vanity domain, e.g.
 * `https://www.some-movie.de/presseheft.pdf`, proxied from the CDN by the
 * landing page app's `/hosted-file` route. The slug is the public URL path, the
 * asset path is the CDN key it resolves to.
 *
 * The indirection is the point: re-pointing a slug at a newly uploaded,
 * differently named key takes effect within minutes, whereas overwriting an S3
 * object keeps serving the old bytes for up to a week — the CDN cache policy
 * has a one-day MinTTL and a seven-day DefaultTTL, uploads carry no
 * `Cache-Control`, and no invalidation runs for assets uploaded through the API.
 */
export type LandingPageFile = {
    /**
     * Path under the vanity domain. No leading slash, lowercase, and it must
     * carry a file extension — interception is extension-based, so a slug
     * without one would never be routed.
     * e.g. `'presseheft.pdf'`, `'presse/kit.zip'`
     */
    slug: string;
    /**
     * CDN asset path exactly as the asset manager returns it, i.e. `'/' + <s3 key>`.
     * Deliberately not an absolute URL: the value is fetched server-side, so
     * storing a path makes off-origin fetching structurally impossible and keeps
     * the entry portable between the local CDN and production.
     */
    assetPath: AssetItemDto['path'];
    /** Admin-facing label only. Never rendered on the landing page. */
    title?: string;
    /** `true` serves the file as `Content-Disposition: attachment` instead of `inline`. */
    download?: boolean;
};

export type WatchMoreVideo = {
    name: string;
    key: string;
    site: 'YouTube' | 'Vimeo';
    duration?: string;
};

export type CompetitionConfiguration = {
    textTop: string;
    /**
     * Raw HTML rendered between the image and the terms text, for embeds a distributor drops in
     * per campaign — currently a Tally entry form. Unlike the markdown fields this one executes
     * `<script>` tags, because embed snippets rely on their loader to fill in the iframe `src`
     * and to keep its height in sync. Trusted input: only Cineamo admins edit the config.
     */
    html?: string;
    textBottom: string;
    imageUrlDesktop: string;
    imageUrlMobile: string;
    buttonText: string;
    buttonUrl: string;
};

export type LandingPagePartner = {
    name: string;
    logoUrl: string;
    link?: string;
};

/**
 * A place where the movie can be watched or bought at home — a streaming service, a VOD
 * store, a DVD shop. Rendered as a logo in the home-entertainment section.
 */
export type HomeEntertainmentOffer = {
    name: string;
    logoUrl: string;
    link?: string;
};

/**
 * Turns the page into a home-entertainment page: once a movie has left the cinemas there is
 * nothing to sell tickets for, so the hero's "Find Showtimes" button, the showtimes finder and
 * the menu's "Tickets" entry are replaced by a CTA that jumps to the offers section below. The
 * section itself mirrors the partner-logos layout.
 *
 * The replacement kicks in as soon as there is at least one offer to jump to; leave the whole
 * block unset (or without offers) to keep the page in cinema mode.
 */
export type HomeEntertainmentConfiguration = {
    /** Section headline, e.g. "Available at". Falls back to the built-in label. */
    headline?: Localized<string> | null;
    /** Hero button and menu label. Falls back to the headline. */
    buttonText?: Localized<string> | null;
    offers: HomeEntertainmentOffer[];
};

export enum HiddenSections {
    TRAILER = 'TRAILER',
    WATCH_MORE = 'WATCH_MORE',
    IMAGES_GALLERY = 'IMAGES_GALLERY',
    COLLAPSIBLE_INFO = 'COLLAPSIBLE_INFO'
}

/**
 * The slot on the movie landing page where a custom banner is rendered.
 * Each value maps to a gap between two rendered sections (see Page.tsx).
 */
export enum MovieBannerPlacement {
    BELOW_HERO = 'BELOW_HERO',
    AFTER_TRAILER = 'AFTER_TRAILER',
    AFTER_WATCH_MORE = 'AFTER_WATCH_MORE',
    AFTER_GALLERY = 'AFTER_GALLERY',
    AFTER_GRETA = 'AFTER_GRETA',
    AFTER_PRESS_QUOTES = 'AFTER_PRESS_QUOTES',
    AFTER_TOUR = 'AFTER_TOUR',
    AFTER_COMPETITION = 'AFTER_COMPETITION',
    AFTER_COLLAPSIBLE = 'AFTER_COLLAPSIBLE',
    ABOVE_FOOTER = 'ABOVE_FOOTER'
}

/**
 * An operator-supplied promotional banner with separate desktop/mobile images
 * and a chosen placement on the movie landing page. Mirrors the cinema-website
 * "own banners" feature (CinemaPromotion) but stored in the JSON config.
 * Only a single banner is supported for now.
 */
export type CustomBannerConfiguration = {
    imageUrlDesktop: string;
    imageUrlMobile: string;
    link?: string;
    imageDescription?: string;
    placement: MovieBannerPlacement;
};

export type ShowtimesFinderHeader = 'artwork' | 'primaryColor' | 'secondaryColor';

export enum InitiallyExpandedSection {
    NONE = 'NONE',
    CAST_AND_CREW = 'CAST_AND_CREW',
    INFO = 'INFO',
    TEACHING_MATERIALS = 'TEACHING_MATERIALS'
}

/**
 * The JSON blob stored in the `landingPageConfiguration` column.
 * Does NOT include `movieId` or `domain` — those are separate DB fields.
 */
export type LandingPageConfigurationJson = {
    /** GA4 measurement ID (`G-XXXXXXX`) — loaded through gtag.js, in addition to Cineamo's own. */
    gTagId?: string;
    /**
     * Google Tag Manager *container* ID (`GTM-XXXXXXX`). A container is loaded through gtm.js
     * and manages its own tags, so it cannot be expressed as a `gTagId`. Independent of
     * `gTagId`: a distributor can run only a container, only their own GA4 property, or both.
     */
    gtmContainerId?: string;
    facebookPixelId?: string;
    tiktokPixelId?: string;
    /** Reddit Ads pixel ID (`a2_...`). */
    redditPixelId?: string;
    movieTourId?: string;
    style: MovieLandingPageStyle;
    faviconUrl: string;
    genres: CustomGenre[];
    hiddenSections?: Array<HiddenSections>;
    initiallyExpandedSection?: InitiallyExpandedSection;
    trailer?: WatchMoreVideo;
    backToTopLinkColor?: 'primary' | 'secondary';
    topBanner: TopBannerConfiguration | false;
    enableTrailerAutoplay: boolean;
    titleArtworkUrl: string;
    titleArtworkOrientation: 'landscape' | 'portrait';
    showtimesFinderHeader?: ShowtimesFinderHeader;
    // Opt-out flag for the showtimes finder's PREVIEWS tab, which lists showings from now up to
    // the movie's release date. Only an explicit `false` hides it: an unset value means the tab is
    // on, so the configs written before it became the default keep working without a re-save. The
    // tab still only appears while the movie is unreleased and actually has preview showings, so
    // leaving it on cannot produce an empty tab.
    showtimesFinderShowPremieres?: boolean;
    heroBackdropUrl: string;
    // @deprecated use heroContentStyleList instead
    heroContentStyle?: CSSProperties;
    heroContentStyleList?: string;
    heroContentStyleListMobile?: string;
    heroButtonsStyleList?: string;
    heroButtonsStyleListMobile?: string;
    movieBackdropUrl: string;
    movieOverview: Localized<string>;
    movieHint?: Localized<string> | null;
    imageGallery: ImageDto['filePath'][];
    cineamoBanner?: {
        desktopUrl?: string;
        mobileUrl?: string;
    };
    customBanner?: CustomBannerConfiguration | null;
    instagramLink?: string;
    facebookLink?: string;
    tiktokLink?: string;
    youtubeLink?: string;
    b2bPreviewUrl?: string;
    pressQuotes: Localized<PressQuote[]> | null;
    teachingMaterials: Localized<{
        // Optional custom section headline; falls back to the default
        // "Teaching Materials"/"Unterrichtsmaterialien" label when empty.
        headline?: string;
        description: string;
        materials: TeachingMaterial[];
    }> | null;
    /**
     * Files reachable under this landing page's own domain. This only makes the
     * URLs resolve — nothing is rendered as a page section. Once a mapping
     * exists, `teachingMaterials[].downloadLink` can point at `/presseheft.pdf`
     * instead of a raw CDN URL.
     */
    files?: LandingPageFile[] | null;
    /**
     * Footer link to the imprint. The distributor, not Cineamo, is the legal provider of a movie
     * landing page under § 5 DDG, so this has to be able to point at the distributor's own
     * imprint on their own site. Unset falls back to Cineamo's imprint.
     */
    imprintUrl?: string;
    /** Footer link to the privacy policy. Same reasoning as {@link imprintUrl}. */
    dataProtectionUrl?: string;
    distributor: {
        name: string;
        logoUrl: string;
        homepageUrl: string;
    } | null;
    watchMoreVideos: Localized<WatchMoreVideo[]> | null;
    competition: Localized<CompetitionConfiguration> | null;
    partnerHeadline?: Localized<string> | null;
    partners?: LandingPagePartner[] | null;
    homeEntertainment?: HomeEntertainmentConfiguration | null;
};

/**
 * Composed type merging the JSON config with the separate DB fields.
 * Used by frontend components that need the full configuration.
 */
export type DomainMovieConfiguration = LandingPageConfigurationJson & {
    movieId: number;
    domain: string;
};

export type MovieLandingPageConfiguration = {
    movie: CachedMovieData;
    movieDto: MovieDto;
    domainConfiguration: DomainMovieConfiguration;
    timeZone: string;
};
